import * as THREE from 'three';

/**
 * Brick / pixel palette — LEGO-like: flat colors, no smooth gradients.
 * White hull, red markings, warm grey panels, yellow thrusters & lights.
 */
const WHITE = 0xf2f2f0;
const WHITE_WARM = 0xe8e6e1;
const GREY_LIGHT = 0xb0b4ba;
const GREY = 0x7a8088;
const GREY_DARK = 0x4a4f57;
const RED = 0xc62828;
const RED_DEEP = 0x8e1b1b;
const YELLOW = 0xffd54f;
const YELLOW_HOT = 0xfff59d;

type Mats = {
  white: THREE.MeshBasicMaterial;
  whiteWarm: THREE.MeshBasicMaterial;
  greyLight: THREE.MeshBasicMaterial;
  grey: THREE.MeshBasicMaterial;
  greyDark: THREE.MeshBasicMaterial;
  red: THREE.MeshBasicMaterial;
  redDeep: THREE.MeshBasicMaterial;
  yellow: THREE.MeshBasicMaterial;
  yellowHot: THREE.MeshBasicMaterial;
};

function makeMats(): Mats {
  return {
    white: new THREE.MeshBasicMaterial({ color: WHITE }),
    whiteWarm: new THREE.MeshBasicMaterial({ color: WHITE_WARM }),
    greyLight: new THREE.MeshBasicMaterial({ color: GREY_LIGHT }),
    grey: new THREE.MeshBasicMaterial({ color: GREY }),
    greyDark: new THREE.MeshBasicMaterial({ color: GREY_DARK }),
    red: new THREE.MeshBasicMaterial({ color: RED }),
    redDeep: new THREE.MeshBasicMaterial({ color: RED_DEEP }),
    yellow: new THREE.MeshBasicMaterial({ color: YELLOW }),
    yellowHot: new THREE.MeshBasicMaterial({ color: YELLOW_HOT }),
  };
}

/** Axis-aligned brick — the atomic “stud brick” unit for this model. */
function brick(
  parent: THREE.Object3D,
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set(x, y, z);
  parent.add(mesh);
  return mesh;
}

/** Classic LEGO stud (low-seg cylinder reads as a pixel nubs). */
function stud(
  parent: THREE.Object3D,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  s = 0.14,
) {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(s * 0.55, s * 0.55, s * 0.35, 6), mat);
  mesh.position.set(x, y, z);
  parent.add(mesh);
  return mesh;
}

/**
 * Procedural LEGO / pixel-art X-wing fighter (all boxy bricks, no smooth cones).
 * Local +Z is nose-forward; ArcadeGame orients it down the corridor (-Z).
 * Overall envelope matches the previous craft so scale 1.6 still fits the corridor.
 */
export function createSpaceship(): THREE.Group {
  const ship = new THREE.Group();
  ship.name = 'arcade-spaceship';
  const m = makeMats();

  buildFuselage(ship, m);
  buildCockpit(ship, m);
  buildNose(ship, m);
  buildSpineAndMarkings(ship, m);
  buildWings(ship, m);

  // Approximate collision radius for gameplay (same ballpark as before)
  ship.userData.radius = 2.2;

  return ship;
}

/** Central brick hull — white with grey side plating. */
function buildFuselage(ship: THREE.Group, m: Mats) {
  // Core white body (main 2×4-ish brick stack along Z)
  brick(ship, 1.05, 0.85, 3.6, m.white, 0, 0, 0.1);
  // Slightly wider mid plate
  brick(ship, 1.25, 0.35, 2.4, m.whiteWarm, 0, -0.28, -0.15);
  // Underside grey tech plate
  brick(ship, 0.95, 0.2, 3.0, m.grey, 0, -0.48, 0.05);

  // Side grey panel bricks (left / right)
  brick(ship, 0.18, 0.55, 2.8, m.greyLight, 0.58, 0.05, 0.0);
  brick(ship, 0.18, 0.55, 2.8, m.greyLight, -0.58, 0.05, 0.0);
  // Darker inset greeble strips
  brick(ship, 0.08, 0.22, 1.6, m.greyDark, 0.68, -0.05, 0.3);
  brick(ship, 0.08, 0.22, 1.6, m.greyDark, -0.68, -0.05, 0.3);

  // Rear engine housing block
  brick(ship, 1.15, 0.7, 0.7, m.grey, 0, -0.05, -1.95);
  brick(ship, 0.9, 0.45, 0.35, m.greyDark, 0, -0.05, -2.35);

  // Row of studs on the dorsal hull (LEGO signature)
  const studZ = [-1.2, -0.7, -0.2, 0.3, 0.8, 1.2];
  for (const z of studZ) {
    stud(ship, m.white, 0.22, 0.52, z, 0.13);
    stud(ship, m.white, -0.22, 0.52, z, 0.13);
  }
}

/** Blocky canopy — stepped dark grey “glass” bricks + red frame. */
function buildCockpit(ship: THREE.Group, m: Mats) {
  // Frame base
  brick(ship, 0.72, 0.18, 1.35, m.redDeep, 0, 0.48, 0.35);
  // Stepped canopy bricks (rear → front, rising then tapering)
  brick(ship, 0.62, 0.28, 0.45, m.greyDark, 0, 0.68, 0.0);
  brick(ship, 0.55, 0.32, 0.4, m.greyDark, 0, 0.72, 0.38);
  brick(ship, 0.45, 0.26, 0.35, m.greyDark, 0, 0.68, 0.72);
  // Tiny yellow HUD brick
  brick(ship, 0.18, 0.1, 0.12, m.yellow, 0, 0.88, 0.4);
  // Side canopy frame rails
  brick(ship, 0.08, 0.28, 1.1, m.red, 0.32, 0.62, 0.35);
  brick(ship, 0.08, 0.28, 1.1, m.red, -0.32, 0.62, 0.35);
  // Pilot-block silhouette (dark)
  brick(ship, 0.28, 0.2, 0.22, m.greyDark, 0, 0.55, 0.25);
}

/** Stepped brick nose instead of a smooth cone. */
function buildNose(ship: THREE.Group, m: Mats) {
  brick(ship, 0.95, 0.7, 0.55, m.white, 0, 0.0, 2.15);
  brick(ship, 0.75, 0.55, 0.45, m.whiteWarm, 0, 0.0, 2.55);
  brick(ship, 0.55, 0.4, 0.4, m.white, 0, 0.0, 2.9);
  brick(ship, 0.35, 0.28, 0.35, m.greyLight, 0, 0.0, 3.2);
  // Red tip brick
  brick(ship, 0.22, 0.18, 0.28, m.red, 0, 0.0, 3.45);
  // Yellow sensor / rangefinder brick on the side (R2-ish accent, simplified)
  brick(ship, 0.16, 0.2, 0.16, m.yellow, 0.42, 0.28, 2.35);
  brick(ship, 0.1, 0.12, 0.1, m.greyDark, 0.42, 0.4, 2.35);
  // Underside intake bricks
  brick(ship, 0.4, 0.15, 0.5, m.greyDark, 0, -0.35, 2.4);
}

/** Red stripe spine + rear yellow thruster cluster on the body. */
function buildSpineAndMarkings(ship: THREE.Group, m: Mats) {
  // Red dorsal stripe (classic rebel marking, brick-width)
  brick(ship, 0.22, 0.12, 2.8, m.red, 0, 0.48, -0.15);
  brick(ship, 0.28, 0.1, 0.5, m.redDeep, 0, 0.5, 1.35);
  // Yellow running lights along the sides
  brick(ship, 0.1, 0.1, 0.14, m.yellow, 0.62, 0.25, 1.4);
  brick(ship, 0.1, 0.1, 0.14, m.yellow, -0.62, 0.25, 1.4);
  brick(ship, 0.1, 0.1, 0.14, m.yellowHot, 0.62, 0.25, -1.5);
  brick(ship, 0.1, 0.1, 0.14, m.yellowHot, -0.62, 0.25, -1.5);

  // Center rear thruster (small yellow exhaust bricks)
  brick(ship, 0.35, 0.3, 0.2, m.greyDark, 0, -0.05, -2.55);
  brick(ship, 0.25, 0.22, 0.18, m.yellow, 0, -0.05, -2.7);
  brick(ship, 0.16, 0.14, 0.12, m.yellowHot, 0, -0.05, -2.82);
}

/**
 * Four S-foil wings in X layout — each is a stack of flat bricks with
 * red edge markings, grey laser cannon, and yellow engine glow.
 *
 * Geometry is authored along +X (outboard). `side` is +1 / -1 for mirror.
 * Group origin is the **shoulder joint on the fuselage** so rotZ pivots at
 * the body (not mid-wing), which keeps the foils visually attached.
 */
function buildWings(ship: THREE.Group, m: Mats) {
  // Hull side is ~0.62–0.68; mount slightly into the side panels so hinges seal.
  const shoulderX = 0.58;
  const foils: Array<{ side: 1 | -1; y: number; z: number; rotZ: number }> = [
    { side: 1, y: 0.38, z: -0.2, rotZ: 0.35 },
    { side: 1, y: -0.38, z: -0.2, rotZ: -0.35 },
    { side: -1, y: 0.38, z: -0.2, rotZ: -0.35 },
    { side: -1, y: -0.38, z: -0.2, rotZ: 0.35 },
  ];

  for (const foil of foils) {
    const s = foil.side;

    // Fixed hardpoint on the hull (does not rotate with the foil) — seals the joint
    brick(ship, 0.28, 0.34, 0.9, m.grey, s * (shoulderX - 0.02), foil.y, foil.z);
    brick(ship, 0.2, 0.26, 0.7, m.greyDark, s * (shoulderX + 0.12), foil.y, foil.z + 0.02);

    const wing = new THREE.Group();
    // Pivot at the body shoulder so dihedral keeps the root planted on the hull
    wing.position.set(s * shoulderX, foil.y, foil.z);
    wing.rotation.z = foil.rotZ;
    ship.add(wing);

    // Hinge / pylon stack — starts at local 0 (body) and steps outboard
    brick(wing, 0.4, 0.32, 0.8, m.grey, s * 0.18, 0, 0.02);
    brick(wing, 0.45, 0.28, 0.72, m.greyLight, s * 0.48, 0, 0.04);
    brick(wing, 0.35, 0.24, 0.65, m.greyDark, s * 0.72, 0.02, 0.0);
    // Small red joint stud brick (S-foil actuator look)
    brick(wing, 0.18, 0.18, 0.35, m.redDeep, s * 0.55, 0.14, 0.15);

    // Main white wing plate — continues from pylon tip outward
    brick(wing, 2.15, 0.14, 0.95, m.white, s * 1.85, 0, 0);
    // Secondary thinner plate on top
    brick(wing, 1.9, 0.1, 0.7, m.whiteWarm, s * 1.9, 0.1, 0.05);
    // Red leading-edge stripe
    brick(wing, 2.05, 0.08, 0.16, m.red, s * 1.85, 0.02, 0.42);
    // Red rebel band near tip
    brick(wing, 0.35, 0.16, 0.85, m.red, s * 2.75, 0.02, 0);
    // Grey panel near mid-wing
    brick(wing, 0.5, 0.12, 0.75, m.greyLight, s * 1.2, 0.08, 0);
    // Studs on wing top
    stud(wing, m.white, s * 1.35, 0.2, 0.15, 0.11);
    stud(wing, m.white, s * 1.85, 0.2, 0.15, 0.11);
    stud(wing, m.white, s * 2.35, 0.2, 0.15, 0.11);

    // Engine pod at wing tip
    const podX = s * 2.95;
    brick(wing, 0.42, 0.42, 0.95, m.grey, podX, 0, -0.15);
    brick(wing, 0.36, 0.36, 0.25, m.greyDark, podX, 0, -0.65);
    brick(wing, 0.3, 0.3, 0.18, m.yellow, podX, 0, -0.85);
    brick(wing, 0.22, 0.22, 0.14, m.yellowHot, podX, 0, -0.98);
    brick(wing, 0.38, 0.38, 0.12, m.redDeep, podX, 0, 0.38);

    // Laser cannon along wing tip
    brick(wing, 0.14, 0.14, 1.35, m.greyDark, podX, 0.22, 0.55);
    brick(wing, 0.18, 0.18, 0.22, m.grey, podX, 0.22, 1.2);
    brick(wing, 0.12, 0.12, 0.2, m.red, podX, 0.22, 1.4);
    brick(wing, 0.08, 0.08, 0.08, m.yellow, podX, 0.34, 0.9);
  }
}
